import type { EngagementAnswerType, EngagementScaleType } from '../slice/AddEngagementQuestion.slice';

const SCALE_VALUES_OPTIONS_FOR_EMOJI_BASED = [
  { value: '3', label: '3-point' },
  { value: '4', label: '4-point' },
  { value: '5', label: '5-point' },
];

const SCALE_VALUES_OPTIONS_FOR_SCALE_BASED = [
  { value: '3', label: '3-point' },
  { value: '5', label: '5-point' },
  { value: '7', label: '7-point' },
  { value: '10', label: 'NPS (0-10)' },
];

const SCALE_VALUES_OPTIONS_FOR_CHOICES_BASED = [
  { value: '2', label: '2-point' },
  { value: '3', label: '3-point' },
  { value: '4', label: '4-point' },
  { value: '5', label: '5-point' },
  { value: '6', label: '6-point' },
  { value: '7', label: '7-point' },
  { value: '8', label: '8-point' },
  { value: '9', label: '9-point' },
  { value: '10', label: '10-point' },
];

const SCALE_VALUE_OPTIONS_FOR_OPEN_ENDED = [];

export const SCALE_VALUE_OPTIONS: Record<
  EngagementAnswerType,
  Array<{
    value: string;
    label: string;
  }>
> = {
  EMOJI: SCALE_VALUES_OPTIONS_FOR_EMOJI_BASED,
  NUMBER_RATING: SCALE_VALUES_OPTIONS_FOR_SCALE_BASED,
  DROP_DOWN: SCALE_VALUES_OPTIONS_FOR_CHOICES_BASED,
  NONE: SCALE_VALUE_OPTIONS_FOR_OPEN_ENDED,
};

const AGREE_DISAGREE = {
  3: [
    { score: 1, displayOption: 1, description: 'Disagree' },
    { score: 2, displayOption: 2, description: 'Neutral' },
    { score: 3, displayOption: 3, description: 'Agree' },
  ],
  4: [
    { score: 1, displayOption: 1, description: 'Strongly Disagree' },
    { score: 2, displayOption: 2, description: 'Disagree' },
    { score: 4, displayOption: 4, description: 'Agree' },
    { score: 5, displayOption: 5, description: 'Strongly Agree' },
  ],
  5: [
    { score: 1, displayOption: 1, description: 'Strongly Disagree' },
    { score: 2, displayOption: 2, description: 'Disagree' },
    { score: 3, displayOption: 3, description: 'Neutral' },
    { score: 4, displayOption: 4, description: 'Agree' },
    { score: 5, displayOption: 5, description: 'Strongly Agree' },
  ],
  7: [
    { score: 1, displayOption: 1, description: 'Strongly Disagree' },
    { score: 2, displayOption: 2, description: 'Disagree' },
    { score: 3, displayOption: 3, description: 'Somewhat Disagree' },
    { score: 4, displayOption: 4, description: 'Neutral' },
    { score: 5, displayOption: 5, description: 'Somewhat Agree' },
    { score: 6, displayOption: 6, description: 'Agree' },
    { score: 7, displayOption: 7, description: 'Strongly Agree' },
  ],
};

const NEVER_ALWAYS = {
  3: [
    { score: 1, displayOption: 1, description: 'Never' },
    { score: 2, displayOption: 2, description: 'Sometimes' },
    { score: 3, displayOption: 3, description: 'Always' },
  ],
  4: [
    { score: 1, displayOption: 1, description: 'Never' },
    { score: 2, displayOption: 2, description: 'Rarely' },
    { score: 4, displayOption: 4, description: 'Often' },
    { score: 5, displayOption: 5, description: 'Always' },
  ],
  5: [
    { score: 1, displayOption: 1, description: 'Never' },
    { score: 2, displayOption: 2, description: 'Rarely' },
    { score: 3, displayOption: 3, description: 'Sometimes' },
    { score: 4, displayOption: 4, description: 'Often' },
    { score: 5, displayOption: 5, description: 'Always' },
  ],
  7: [
    { score: 1, displayOption: 1, description: 'Never' },
    { score: 2, displayOption: 2, description: 'Almost Never' },
    { score: 3, displayOption: 3, description: 'Rarely' },
    { score: 4, displayOption: 4, description: 'Occasionally' },
    { score: 5, displayOption: 5, description: 'Sometimes' },
    { score: 6, displayOption: 6, description: 'Frequently' },
    { score: 7, displayOption: 7, description: 'Always' },
  ],
};

const DISSATISFIED_SATISFIED = {
  3: [
    { score: 1, displayOption: 1, description: 'Dissatisfied' },
    { score: 2, displayOption: 2, description: 'Neutral' },
    { score: 3, displayOption: 3, description: 'Satisfied' },
  ],
  4: [
    { score: 1, displayOption: 1, description: 'Very Dissatisfied' },
    { score: 2, displayOption: 2, description: 'Dissatisfied' },
    { score: 4, displayOption: 4, description: 'Satisfied' },
    { score: 5, displayOption: 5, description: 'Very Satisfied' },
  ],
  5: [
    { score: 1, displayOption: 1, description: 'Very Dissatisfied' },
    { score: 2, displayOption: 2, description: 'Dissatisfied' },
    { score: 3, displayOption: 3, description: 'Neutral' },
    { score: 4, displayOption: 4, description: 'Satisfied' },
    { score: 5, displayOption: 5, description: 'Very Satisfied' },
  ],
  7: [
    { score: 1, displayOption: 1, description: 'Extremely Dissatisfied' },
    { score: 2, displayOption: 2, description: 'Very Dissatisfied' },
    { score: 3, displayOption: 3, description: 'Dissatisfied' },
    { score: 4, displayOption: 4, description: 'Neutral' },
    { score: 5, displayOption: 5, description: 'Satisfied' },
    { score: 6, displayOption: 6, description: 'Very Satisfied' },
    { score: 7, displayOption: 7, description: 'Extremely Satisfied' },
  ],
};

const UNLIKELY_LIKELY = {
  3: [
    { score: 1, displayOption: 1, description: 'Unlikely' },
    { score: 2, displayOption: 2, description: 'Neutral' },
    { score: 3, displayOption: 3, description: 'Likely' },
  ],
  4: [
    { score: 1, displayOption: 1, description: 'Not at all Likely' },
    { score: 2, displayOption: 2, description: 'Slightly Likely' },
    { score: 4, displayOption: 4, description: 'Likely' },
    { score: 5, displayOption: 5, description: 'Extremely Likely' },
  ],
  5: [
    { score: 1, displayOption: 1, description: 'Not at all Likely' },
    { score: 2, displayOption: 2, description: 'Slightly Likely' },
    { score: 3, displayOption: 3, description: 'Neutral' },
    { score: 4, displayOption: 4, description: 'Likely' },
    { score: 5, displayOption: 5, description: 'Extremely Likely' },
  ],
  7: [
    { score: 1, displayOption: 1, description: 'Not at all Likely' },
    { score: 2, displayOption: 2, description: 'Very Unlikely' },
    { score: 3, displayOption: 3, description: 'Unlikely' },
    { score: 4, displayOption: 4, description: 'Neutral' },
    { score: 5, displayOption: 5, description: 'Likely' },
    { score: 6, displayOption: 6, description: 'Very Likely' },
    { score: 7, displayOption: 7, description: 'Extremely Likely' },
  ],
};

const POOR_EXCELLENT = {
  3: [
    { score: 1, displayOption: 1, description: 'Poor' },
    { score: 2, displayOption: 2, description: 'Neutral' },
    { score: 3, displayOption: 3, description: 'Excellent' },
  ],
  4: [
    { score: 1, displayOption: 1, description: 'Very Poor' },
    { score: 2, displayOption: 2, description: 'Poor' },
    { score: 4, displayOption: 4, description: 'Good' },
    { score: 5, displayOption: 5, description: 'Excellent' },
  ],
  5: [
    { score: 1, displayOption: 1, description: 'Very Poor' },
    { score: 2, displayOption: 2, description: 'Poor' },
    { score: 3, displayOption: 3, description: 'Neutral' },
    { score: 4, displayOption: 4, description: 'Good' },
    { score: 5, displayOption: 5, description: 'Excellent' },
  ],
  7: [
    { score: 1, displayOption: 1, description: 'Extremely Poor' },
    { score: 2, displayOption: 2, description: 'Very Poor' },
    { score: 3, displayOption: 3, description: 'Poor' },
    { score: 4, displayOption: 4, description: 'Neutral' },
    { score: 5, displayOption: 5, description: 'Good' },
    { score: 6, displayOption: 6, description: 'Very Good' },
    { score: 7, displayOption: 7, description: 'Excellent' },
  ],
};

const UNIMPORTANT_IMPORTANT = {
  3: [
    { score: 1, displayOption: 1, description: 'Unimportant' },
    { score: 2, displayOption: 2, description: 'Neutral' },
    { score: 3, displayOption: 3, description: 'Important' },
  ],
  4: [
    { score: 1, displayOption: 1, description: 'Not Important' },
    { score: 2, displayOption: 2, description: 'Slightly Important' },
    { score: 4, displayOption: 4, description: 'Important' },
    { score: 5, displayOption: 5, description: 'Very Important' },
  ],
  5: [
    { score: 1, displayOption: 1, description: 'Not Important' },
    { score: 2, displayOption: 2, description: 'Slightly Important' },
    { score: 3, displayOption: 3, description: 'Neutral' },
    { score: 4, displayOption: 4, description: 'Important' },
    { score: 5, displayOption: 5, description: 'Very Important' },
  ],
  7: [
    { score: 1, displayOption: 1, description: 'Not Important at All' },
    { score: 2, displayOption: 2, description: 'Slightly Important' },
    { score: 3, displayOption: 3, description: 'Somewhat Important' },
    { score: 4, displayOption: 4, description: 'Neutral' },
    { score: 5, displayOption: 5, description: 'Somewhat Important' },
    { score: 6, displayOption: 6, description: 'Important' },
    { score: 7, displayOption: 7, description: 'Very Important' },
  ],
};

const NOT_CONFIDENT_CONFIDENT = {
  3: [
    { score: 1, displayOption: 1, description: 'Not Confident' },
    { score: 2, displayOption: 2, description: 'Neutral' },
    { score: 3, displayOption: 3, description: 'Confident' },
  ],
  4: [
    { score: 1, displayOption: 1, description: 'Not Confident at All' },
    { score: 2, displayOption: 2, description: 'Slightly Confident' },
    { score: 4, displayOption: 4, description: 'Confident' },
    { score: 5, displayOption: 5, description: 'Very Confident' },
  ],
  5: [
    { score: 1, displayOption: 1, description: 'Not Confident at All' },
    { score: 2, displayOption: 2, description: 'Slightly Confident' },
    { score: 3, displayOption: 3, description: 'Neutral' },
    { score: 4, displayOption: 4, description: 'Confident' },
    { score: 5, displayOption: 5, description: 'Very Confident' },
  ],
  7: [
    { score: 1, displayOption: 1, description: 'Not Confident at All' },
    { score: 2, displayOption: 2, description: 'Very Slightly Confident' },
    { score: 3, displayOption: 3, description: 'Slightly Confident' },
    { score: 4, displayOption: 4, description: 'Neutral' },
    { score: 5, displayOption: 5, description: 'Moderately Confident' },
    { score: 6, displayOption: 6, description: 'Confident' },
    { score: 7, displayOption: 7, description: 'Very Confident' },
  ],
};

const EASY_DIFFICULT = {
  3: [
    { score: 1, displayOption: 1, description: 'Easy' },
    { score: 2, displayOption: 2, description: 'Neutral' },
    { score: 3, displayOption: 3, description: 'Difficult' },
  ],
  4: [
    { score: 1, displayOption: 1, description: 'Very Easy' },
    { score: 2, displayOption: 2, description: 'Easy' },
    { score: 4, displayOption: 4, description: 'Difficult' },
    { score: 5, displayOption: 5, description: 'Very Difficult' },
  ],
  5: [
    { score: 1, displayOption: 1, description: 'Very Easy' },
    { score: 2, displayOption: 2, description: 'Easy' },
    { score: 3, displayOption: 3, description: 'Neutral' },
    { score: 4, displayOption: 4, description: 'Difficult' },
    { score: 5, displayOption: 5, description: 'Very Difficult' },
  ],
  7: [
    { score: 1, displayOption: 1, description: 'Extremely Easy' },
    { score: 2, displayOption: 2, description: 'Very Easy' },
    { score: 3, displayOption: 3, description: 'Easy' },
    { score: 4, displayOption: 4, description: 'Neutral' },
    { score: 5, displayOption: 5, description: 'Difficult' },
    { score: 6, displayOption: 6, description: 'Very Difficult' },
    { score: 7, displayOption: 7, description: 'Extremely Difficult' },
  ],
};

const NO_UNDERSTANDING_FULL_UNDERSTANDING = {
  3: [
    { score: 1, displayOption: 1, description: 'No Understanding' },
    { score: 2, displayOption: 2, description: 'Neutral' },
    { score: 3, displayOption: 3, description: 'Full Understanding' },
  ],
  4: [
    { score: 1, displayOption: 1, description: 'No Understanding' },
    { score: 2, displayOption: 2, description: 'Some Understanding' },
    { score: 4, displayOption: 4, description: 'Good Understanding' },
    { score: 5, displayOption: 5, description: 'Full Understanding' },
  ],
  5: [
    { score: 1, displayOption: 1, description: 'No Understanding' },
    { score: 2, displayOption: 2, description: 'Some Understanding' },
    { score: 3, displayOption: 3, description: 'Neutral' },
    { score: 4, displayOption: 4, description: 'Good Understanding' },
    { score: 5, displayOption: 5, description: 'Full Understanding' },
  ],
  7: [
    { score: 1, displayOption: 1, description: 'No Understanding' },
    { score: 2, displayOption: 2, description: 'Very Limited Understanding' },
    { score: 3, displayOption: 3, description: 'Limited Understanding' },
    { score: 4, displayOption: 4, description: 'Neutral' },
    { score: 5, displayOption: 5, description: 'Moderate Understanding' },
    { score: 6, displayOption: 6, description: 'Good Understanding' },
    { score: 7, displayOption: 7, description: 'Full Understanding' },
  ],
};

export const NPS_SCALE_OPTIONS = [
  { score: 0, displayOption: 0, description: 'Not at all Likely' },
  { score: 1, displayOption: 1, description: '', isDisabled: true },
  { score: 2, displayOption: 2, description: '', isDisabled: true },
  { score: 3, displayOption: 3, description: '', isDisabled: true },
  { score: 4, displayOption: 4, description: '', isDisabled: true },
  { score: 5, displayOption: 5, description: '', isDisabled: true },
  { score: 6, displayOption: 6, description: '', isDisabled: true },
  { score: 7, displayOption: 7, description: '', isDisabled: true },
  { score: 8, displayOption: 8, description: '', isDisabled: true },
  { score: 9, displayOption: 9, description: '', isDisabled: true },
  { score: 10, displayOption: 10, description: 'Extremely Likely' },
];

export const ENGAGEMENT_SCALE_TYPE_OPTIONS: Record<
  EngagementScaleType,
  Record<number, Array<{ score: number; displayOption: number; description: string; isDisabled?: boolean }>>
> = {
  AGREE_DISAGREE,
  DISSATISFIED_SATISFIED,
  EASY_DIFFICULT,
  NEVER_ALWAYS,
  NO_UNDERSTANDING_FULL_UNDERSTANDING,
  NOT_CONFIDENT_CONFIDENT,
  POOR_EXCELLENT,
  UNLIKELY_LIKELY,
  UNIMPORTANT_IMPORTANT,
};

export const MAX_OPTIONS_FOR_NPS = 10;
export const OPEN_ENGAGEMENT_QUESTION_PANEL = 'openEngagementQuestionPanel';
